/**
 * HarvestDashboard Component
 *
 * Main dashboard for harvest recording and yield tracking.
 * Shows batches ready for harvest and overall metrics.
 *
 * User Story: US-P2-007 - Harvest Recording & Yield Tracking
 */

import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { PageLayout } from '@/components/layout'
import {
  Scale,
  AlertCircle,
  Plus,
  RefreshCw,
  Leaf,
  Scissors,
  Zap
} from 'lucide-react'
import { HarvestRecordForm, HarvestHistoryTable, YieldMetricsCard, UnifiedHarvestForm, QuickHarvestModal } from './harvest'
import { ContaminationMetricsCard, QuarantineAlerts } from './contamination'
import { useHarvestBatches } from '@/hooks/useHarvestBatches'
import { supabase } from '@/supabase-client'

// Status badge colours
const statusColors = {
  FRUITING: 'bg-purple-100 text-purple-800',
  FIRST_FLUSH: 'bg-green-100 text-green-800',
  RESTING: 'bg-yellow-100 text-yellow-800',
  SECOND_FLUSH: 'bg-emerald-100 text-emerald-800',
  SPENT: 'bg-gray-100 text-gray-800'
}

// Harvestable statuses
const HARVESTABLE_STATUSES = ['FRUITING', 'FIRST_FLUSH', 'RESTING', 'SECOND_FLUSH']

export default function HarvestDashboard({ user, onLogout }) {
  const { legacyBatches, standardBatches, loading, error, refetch } = useHarvestBatches()
  const [selectedBatch, setSelectedBatch] = useState(null)
  const [showRecordForm, setShowRecordForm] = useState(false)
  const [showUnifiedForm, setShowUnifiedForm] = useState(false)
  const [showQuickHarvest, setShowQuickHarvest] = useState(false)
  const [activeTab, setActiveTab] = useState('ready')
  const [recentHarvests, setRecentHarvests] = useState([])
  const [recentHarvestsLoading, setRecentHarvestsLoading] = useState(true)

  // Fetch recent harvests - all batches with harvest records, regardless of status
  // Includes both in-house production batches and substrate inductions
  const fetchRecentHarvests = useCallback(async () => {
    setRecentHarvestsLoading(true)
    try {
      // Fetch production batches
      const { data: prodData, error: prodError } = await supabase
        .from('production_batches')
        .select(`
          id,
          batch_code,
          current_state,
          total_blocks,
          total_harvest_kg,
          strain:strains (id, code, name)
        `)
        .gt('total_harvest_kg', 0)
        .order('updated_at', { ascending: false })
        .limit(20)

      if (prodError) throw prodError

      // Fetch substrate inductions with harvests
      const { data: subData, error: subError } = await supabase
        .from('substrate_inductions')
        .select(`
          id,
          induction_code,
          status,
          log_count,
          total_harvest_kg,
          strain:strains (id, code, name)
        `)
        .gt('total_harvest_kg', 0)
        .order('updated_at', { ascending: false })
        .limit(10)

      if (subError) throw subError

      // Normalise substrate inductions to match production batch shape
      const substrateRows = (subData || []).map(ind => ({
        id: ind.id,
        batch_code: ind.induction_code,
        current_state: ind.status?.toUpperCase(),
        total_blocks: ind.log_count,
        total_harvest_kg: ind.total_harvest_kg,
        strain: ind.strain,
        _source: 'substrate'
      }))

      const prodRows = (prodData || []).map(b => ({ ...b, _source: 'production' }))

      // Merge and sort by harvest weight (most productive first)
      const merged = [...prodRows, ...substrateRows]
        .sort((a, b) => parseFloat(b.total_harvest_kg) - parseFloat(a.total_harvest_kg))
        .slice(0, 20)

      setRecentHarvests(merged)
    } catch (err) {
      console.error('[HarvestDashboard] Error fetching recent harvests:', err)
    } finally {
      setRecentHarvestsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRecentHarvests()
  }, [fetchRecentHarvests])

  // Combine all batches for display
  const allBatches = [...(legacyBatches || []), ...(standardBatches || [])]

  // Filter batches by harvestable status
  const harvestableBatches = allBatches.filter(b =>
    HARVESTABLE_STATUSES.includes(b.current_state)
  )

  const handleRecordHarvest = (batch) => {
    setSelectedBatch(batch)
    setShowRecordForm(true)
  }

  const refetchAll = useCallback(() => {
    refetch()
    fetchRecentHarvests()
  }, [refetch, fetchRecentHarvests])

  const handleSaveHarvest = () => {
    setShowRecordForm(false)
    setSelectedBatch(null)
    refetchAll()
  }

  const handleUnifiedFormSave = () => {
    setShowUnifiedForm(false)
    refetchAll()
  }

  const handleQuickHarvestSave = () => {
    refetchAll()
  }

  // Header actions
  const headerActions = (
    <>
      <Button variant="outline" onClick={() => setShowQuickHarvest(true)}>
        <Zap className="h-4 w-4 mr-2" />
        Quick Add
      </Button>
      <Button onClick={() => setShowUnifiedForm(true)}>
        <Plus className="h-4 w-4 mr-2" />
        Record Harvest
      </Button>
      <Button onClick={refetchAll} variant="outline" size="sm">
        <RefreshCw className="h-4 w-4 mr-2" />
        Refresh
      </Button>
    </>
  )

  return (
    <PageLayout
      user={user}
      onLogout={onLogout}
      title="Harvests"
      icon={Scissors}
      headerActions={headerActions}
    >
      <div className="h-full overflow-y-auto">
      <div className="space-y-6">
        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Quarantine Alerts */}
        <QuarantineAlerts />

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="ready">Ready to Harvest</TabsTrigger>
            <TabsTrigger value="recent">Recent Harvests</TabsTrigger>
            <TabsTrigger value="contamination">Contamination</TabsTrigger>
          </TabsList>

          {/* Ready to Harvest Tab */}
          <TabsContent value="ready" className="space-y-4">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : harvestableBatches.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Leaf className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium">No batches ready for harvest</h3>
                  <p className="text-muted-foreground text-sm mt-1">
                    Batches will appear here when they reach the fruiting stage
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Batch</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Strain</TableHead>
                    <TableHead className="text-right">Blocks</TableHead>
                    <TableHead className="text-right">Harvested</TableHead>
                    <TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {harvestableBatches.map(batch => (
                    <TableRow key={batch.id}>
                      <TableCell className="font-semibold">{batch.batch_code || batch.id.slice(0, 8)}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[batch.current_state] || 'bg-gray-100'}>
                          {batch.current_state?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{batch.strain?.name || 'Unknown strain'}</TableCell>
                      <TableCell className="text-right">{batch.total_blocks || 0}</TableCell>
                      <TableCell className="text-right font-medium">{(parseFloat(batch.total_harvest_kg) || 0).toFixed(1)} kg</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" onClick={() => handleRecordHarvest(batch)}>
                          <Plus className="h-4 w-4 mr-1" />
                          Record
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>

          {/* Recent Harvests Tab */}
          <TabsContent value="recent" className="space-y-4">
            {recentHarvestsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : recentHarvests.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Scale className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium">No harvests recorded yet</h3>
                  <p className="text-muted-foreground text-sm mt-1">
                    Record your first harvest to start tracking yields
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Batch</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Strain</TableHead>
                    <TableHead className="text-right">Blocks</TableHead>
                    <TableHead className="text-right">Total Yield</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentHarvests.map(batch => (
                    <TableRow key={batch.id}>
                      <TableCell className="font-semibold">
                        {batch.batch_code || batch.id.slice(0, 8)}
                        {batch._source === 'substrate' && (
                          <Badge variant="outline" className="ml-2 text-xs">Substrate</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[batch.current_state] || 'bg-gray-100 text-gray-800'}>
                          {batch.current_state?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{batch.strain?.name || 'Unknown strain'}</TableCell>
                      <TableCell className="text-right">{batch.total_blocks || 0}</TableCell>
                      <TableCell className="text-right font-bold text-green-600">{(parseFloat(batch.total_harvest_kg) || 0).toFixed(1)} kg</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>

          {/* Contamination Tab */}
          <TabsContent value="contamination" className="space-y-4">
            <ContaminationMetricsCard />
          </TabsContent>
        </Tabs>
      </div>
      </div>

        {/* Record Harvest Dialog (for batch-specific recording) */}
        <HarvestRecordForm
          isOpen={showRecordForm}
          onClose={() => {
            setShowRecordForm(false)
            setSelectedBatch(null)
          }}
          onSave={handleSaveHarvest}
          container={selectedBatch ? { id: selectedBatch.id, batch: selectedBatch } : null}
          existingHarvests={[]}
        />

        {/* Unified Harvest Form (header button) */}
        <UnifiedHarvestForm
          isOpen={showUnifiedForm}
          onClose={() => setShowUnifiedForm(false)}
          onSave={handleUnifiedFormSave}
        />

        {/* Quick Harvest Modal (numpad entry) */}
        <QuickHarvestModal
          isOpen={showQuickHarvest}
          onClose={() => setShowQuickHarvest(false)}
          onSave={handleQuickHarvestSave}
        />
    </PageLayout>
  )
}
